import {
  ArrowDownLeft,
  ArrowUpRight,
  Clock,
  DollarSign,
  PieChart,
  TrendingUp,
  Wallet,
} from 'lucide-react';
import { FINANCIAL_KPIS } from './mockFinanceData';

function formatAed(amount) {
  return amount.toLocaleString('en-AE');
}

export default function FinancialKpiSummary({ kpis = FINANCIAL_KPIS }) {
  const paidPct = Math.round(
    (kpis.invoicePaidAmount / (kpis.invoicePaidAmount + kpis.invoicePendingAmount)) * 100,
  );
  const pendingPct = 100 - paidPct;

  return (
    <section
      aria-label="System-wide financial KPI summary"
      className="overflow-hidden rounded-xl border border-logi-grid bg-white backdrop-blur-sm"
    >
      <div className="flex items-center gap-2 border-b border-logi-grid px-4 py-3">
        <TrendingUp className="h-4 w-4 text-emerald-700" strokeWidth={1.75} />
        <div>
          <h3 className="text-sm font-bold text-logi-text">System-Wide Financial KPI Summary</h3>
          <p className="text-[11px] text-logi-text0">{kpis.revenueMonthLabel} · Fleet-wide indicators</p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-2 xl:grid-cols-4">
        <article className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
          <div className="flex items-start justify-between">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-50 ring-1 ring-emerald-500/25">
              <DollarSign className="h-4 w-4 text-emerald-700" strokeWidth={1.75} />
            </div>
            <span className="inline-flex items-center gap-0.5 text-[10px] font-bold text-emerald-700">
              <ArrowUpRight className="h-3 w-3" />
              +8.2%
            </span>
          </div>
          <p className="mt-3 text-[10px] font-semibold uppercase tracking-wider text-logi-text0">
            Total Revenue This Month
          </p>
          <p className="mt-1 font-mono text-2xl font-bold text-emerald-700">
            {formatAed(kpis.totalRevenueMonth)}
            <span className="ml-1 text-xs text-logi-text0">AED</span>
          </p>
        </article>

        <article className="rounded-xl border border-amber-200 bg-amber-50 p-4">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-50 ring-1 ring-amber-500/25">
            <Clock className="h-4 w-4 text-amber-700" strokeWidth={1.75} />
          </div>
          <p className="mt-3 text-[10px] font-semibold uppercase tracking-wider text-logi-text0">
            Outstanding Receivables
          </p>
          <p className="mt-1 font-mono text-2xl font-bold text-amber-700">
            {formatAed(kpis.outstandingReceivables)}
            <span className="ml-1 text-xs text-logi-text0">AED</span>
          </p>
          <div className="mt-2 space-y-0.5 text-[10px] text-logi-text0">
            <p>0–30d: <span className="font-mono text-logi-muted">{formatAed(kpis.receivablesAging.current)}</span></p>
            <p>31–60d: <span className="font-mono text-logi-muted">{formatAed(kpis.receivablesAging.days31to60)}</span></p>
            <p>61+d: <span className="font-mono text-red-700/80">{formatAed(kpis.receivablesAging.days61plus)}</span></p>
          </div>
        </article>

        <article className="rounded-xl border border-logi-accent/25 bg-logi-accent/10 p-4">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-logi-accent/10 ring-1 ring-logi-accent/30">
            <Wallet className="h-4 w-4 text-logi-accent" strokeWidth={1.75} />
          </div>
          <p className="mt-3 text-[10px] font-semibold uppercase tracking-wider text-logi-text0">
            Paid vs Pending Invoices
          </p>
          <div className="mt-2 flex items-baseline gap-3">
            <div>
              <p className="font-mono text-xl font-bold text-emerald-700">{kpis.invoicesPaid}</p>
              <p className="text-[10px] text-logi-text0">Paid</p>
            </div>
            <div>
              <p className="font-mono text-xl font-bold text-amber-700">{kpis.invoicesPending}</p>
              <p className="text-[10px] text-logi-text0">Pending</p>
            </div>
          </div>
          <div className="mt-2">
            <div className="flex h-2 overflow-hidden rounded-full bg-logi-silver/80">
              <div className="bg-emerald-500" style={{ width: `${paidPct}%` }} />
              <div className="bg-amber-500" style={{ width: `${pendingPct}%` }} />
            </div>
            <div className="mt-1 flex justify-between text-[9px] text-logi-text0">
              <span>{formatAed(kpis.invoicePaidAmount)} AED paid</span>
              <span>{formatAed(kpis.invoicePendingAmount)} pending</span>
            </div>
          </div>
        </article>

        <article className="rounded-xl border border-logi-accent/30 bg-logi-accent/10 p-4">
          <div className="flex items-start justify-between">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-logi-accent/10 ring-1 ring-violet-500/25">
              <PieChart className="h-4 w-4 text-logi-accent" strokeWidth={1.75} />
            </div>
            <span className="inline-flex items-center gap-0.5 text-[10px] font-bold text-logi-accent">
              <ArrowDownLeft className="h-3 w-3" />
              {kpis.fleetAssetCount} assets
            </span>
          </div>
          <p className="mt-3 text-[10px] font-semibold uppercase tracking-wider text-logi-text0">
            Avg Profitability / Fleet Asset
          </p>
          <p className="mt-1 font-mono text-2xl font-bold text-logi-accent">
            {kpis.avgProfitabilityPerAsset}
            <span className="ml-1 text-lg">%</span>
          </p>
          <p className="mt-1 text-[10px] text-logi-text0">Net margin across active tanker fleet</p>
        </article>
      </div>
    </section>
  );
}
